import _extends from "@babel/runtime/helpers/extends";
import { Dropdown, FormControl, Icon, MenuItem } from "@icg360/ui-toolkit";
import classnames from "classnames";
import PT from "prop-types";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import useContainsFocus from "../../shared/hooks/use-contains-focus";
import usePost from "../../shared/hooks/use-post";
import useFetch from "../../shared/hooks/use-fetch";
import { getPlace, getPlacePredictions } from "./google-places";
import ClearFormControl from "../../shared/components/clear-form-control";
var NORMALIZE_SIZE = {
  sm: "sm",
  small: "sm",
  lg: "lg",
  large: "lg",
  default: ""
};
var SEARCH_THRESHOLD = 3;
var DROPDOWN_TYPES = {
  DEFAULT: "DEFAULT",
  FALLBACK: "FALLBACK"
};
var stylesOverride = /*#__PURE__*/React.createElement("style", {
  dangerouslySetInnerHTML: {
    __html: "\n      .AddressInput-dropdown li a {\n        line-height: 40px;\n      }\n      .AddressInput-dropdown li p {\n        margin-right: 25px;\n        margin-left: 25px;\n      }\n    "
  }
});

var EmphasizeMatched = function EmphasizeMatched(_ref) {
  var text = _ref.text,
      matched = _ref.matched;
  var list = matched.reduce(function (_ref2, _ref3) {
    var parts = _ref2.parts,
        from = _ref2.from;
    var offset = _ref3.offset,
        length = _ref3.length;
    return {
      parts: [].concat(parts, [{
        type: "text",
        value: text.substring(from, offset)
      }, {
        type: "match",
        value: text.substring(offset, offset + length)
      }]),
      from: offset + length
    };
  }, {
    parts: [],
    from: 0
  });

  if (list.from < text.length) {
    list.parts.push({
      type: "text",
      value: text.substring(list.from)
    });
  }

  return list.parts.filter(function (_ref4) {
    var value = _ref4.value;
    return value;
  }).map(function (_ref5, i) {
    var type = _ref5.type,
        value = _ref5.value;
    return /*#__PURE__*/React.createElement("span", {
      key: i,
      className: type
    }, value);
  });
};

var AddressInput = /*#__PURE__*/forwardRef(function (_ref6, ref) {
  var className = _ref6.className,
      disabled = _ref6.disabled,
      defaultMenuItemCount = _ref6.defaultMenuItemCount,
      fallbackMenuItemCount = _ref6.fallbackMenuItemCount,
      includeEstablishments = _ref6.includeEstablishments,
      inputProps = _ref6.inputProps,
      isLocked = _ref6.isLocked,
      readOnly = _ref6.readOnly,
      onAddress = _ref6.onAddress,
      onBlur = _ref6.onBlur,
      _onChange = _ref6.onChange,
      onEnter = _ref6.onEnter,
      renderDefault = _ref6.renderDefault,
      renderFallback = _ref6.renderFallback,
      size = _ref6.size,
      value = _ref6.value;

  var _useState = useState(-1),
      highlightIndex = _useState[0],
      setHighlightIndex = _useState[1];

  var _inputRef = useRef(null);

  var dropdownRef = useRef(null);
  var hasFocus = useContainsFocus({
    ref: dropdownRef
  });
  var enableSuggestions = !isLocked && value.length >= SEARCH_THRESHOLD;
  useImperativeHandle(ref, function () {
    return {
      focus: function focus() {
        if (_inputRef.current) {
          _inputRef.current.focus();
        }
      }
    };
  }); // PPQ-5066 IE focuses the field but typing is ignored
  // I believe this is because focus is set before readOnly is removed.

  var delayedFocus = useRef(false);
  useEffect(function () {
    if (!isLocked && delayedFocus.current) {
      delayedFocus.current = false;

      _inputRef.current.focus();
    }
  }, [isLocked]);

  var _useFetch = useFetch({
    request: getPlacePredictions,
    using: {
      input: value,
      types: includeEstablishments ? ["geocode", "establishment"] : ["geocode"]
    },
    skipCall: !hasFocus || !enableSuggestions,
    willUpdate: function willUpdate(state) {
      if (state.dataMatchesRequest) {
        setHighlightIndex(state.data && state.data.length ? 0 : -1);
      }

      return state;
    }
  }),
      data = _useFetch[0].data;

  var results = data || [];
  var showSuggestions = enableSuggestions && (!data || !!results.length);

  var _usePost = usePost({
    request: getPlace,
    willUpdate: function willUpdate(state) {
      if (state.data) {
        onAddress(state.data);
        return undefined;
      }

      return state;
    }
  }),
      getAddressData = _usePost[1];

  var onSelect = function onSelect(placeId) {
    getAddressData({
      placeId: placeId,
      isEstablishment: includeEstablishments
    });

    if (_inputRef.current) {
      _inputRef.current.focus();
    }
  };

  var handleInputKeyDown = function handleInputKeyDown(e) {
    var key = e.key,
        metaKey = e.metaKey;

    if (isLocked && !readOnly && !disabled) {
      if (key === "Backspace") {
        e.preventDefault();
        onAddress(null);
      }

      return;
    }

    var highlightMax = calcHighlightMax({
      showSuggestions: showSuggestions,
      enableSuggestions: enableSuggestions,
      resultCount: results.length,
      defaultMenuItemCount: defaultMenuItemCount,
      fallbackMenuItemCount: fallbackMenuItemCount
    });

    if (key === "PageDown" || key === "ArrowDown" && metaKey) {
      e.preventDefault();
      setHighlightIndex(highlightMax);
    } else if (key === "PageUp" || key === "ArrowUp" && metaKey) {
      e.preventDefault();
      setHighlightIndex(-1);
    } else if (key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex(highlightIndex >= highlightMax ? -1 : highlightIndex + 1);
    } else if (key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex(highlightIndex <= -1 ? highlightMax : highlightIndex - 1);
    } else if (key === "Escape") {
      document.body.focus();
    } else if (key === "Enter") {
      e.preventDefault();

      if (showSuggestions && highlightIndex >= 0) {
        onSelect(results[highlightIndex].place_id);
      } else {
        onEnter({
          highlightIndex: highlightIndex,
          type: enableSuggestions ? DROPDOWN_TYPES.FALLBACK : DROPDOWN_TYPES.DEFAULT
        });
      }
    }
  };

  var isOpen = hasFocus && !isLocked && (showSuggestions || enableSuggestions && renderFallback || !enableSuggestions && renderDefault);
  return /*#__PURE__*/React.createElement("div", {
    ref: dropdownRef,
    className: classnames("AddressInput", className, "dropdown", isOpen && "open"),
    "data-size": NORMALIZE_SIZE[size],
    "data-locked": isLocked
  }, /*#__PURE__*/React.createElement(ClearFormControl, {
    onClear: function onClear() {
      onAddress(null);
      delayedFocus.current = true;
    },
    shouldHideButton: !(isLocked && !readOnly && !disabled)
  }, /*#__PURE__*/React.createElement(FormControl // set to "new-password" to stop browsers from using autocomplete.
  // see https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#the_autocomplete_attribute_and_login_fields
  , _extends({
    autoComplete: "new-password",
    disabled: disabled,
    readOnly: readOnly || isLocked,
    bsSize: size === "default" ? undefined : size
  }, inputProps, {
    inputRef: function inputRef(r) {
      // use functional ref because react bootstrap doesn't support objects
      _inputRef.current = r;
    },
    value: value,
    onChange: function onChange(_ref7) {
      var value = _ref7.target.value;

      _onChange(value);

      if (value.length < SEARCH_THRESHOLD && highlightIndex !== -1) {
        setHighlightIndex(-1);
      }
    },
    onBlur: onBlur,
    onKeyDown: handleInputKeyDown,
    title: isLocked ? value : undefined
  }))), /*#__PURE__*/React.createElement(Dropdown.Menu, {
    className: "AddressInput-dropdown"
  }, showSuggestions && results.map(function (_ref8, index) {
    var description = _ref8.description,
        place_id = _ref8.place_id,
        matched_substrings = _ref8.matched_substrings;
    return /*#__PURE__*/React.createElement(MenuItem, {
      active: highlightIndex === index,
      eventKey: place_id,
      key: place_id,
      onSelect: onSelect,
      title: description,
      className: "AddressInput-suggestion"
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "room",
      className: "icon",
      size: "sm"
    }), " ", /*#__PURE__*/React.createElement(EmphasizeMatched, {
      text: description,
      matched: matched_substrings
    }));
  }), enableSuggestions && renderFallback && renderFallback({
    highlightIndex: highlightIndex,
    results: results
  }), !enableSuggestions && renderDefault && renderDefault({
    highlightIndex: highlightIndex,
    results: results
  }), stylesOverride));
});
AddressInput.propTypes = {
  className: PT.string,
  defaultMenuItemCount: PT.number,
  disabled: PT.bool,
  fallbackMenuItemCount: PT.number,
  includeEstablishments: PT.bool,
  inputProps: PT.object,
  isLocked: PT.bool,
  readOnly: PT.bool,
  onAddress: PT.func.isRequired,
  onBlur: PT.func,
  onChange: PT.func.isRequired,
  onEnter: PT.func,
  renderDefault: PT.func,
  renderFallback: PT.func,
  size: PT.oneOf(["sm", "small", "lg", "large", "default"]),
  value: PT.string
};
AddressInput.defaultProps = {
  className: null,
  disabled: false,
  defaultMenuItemCount: 0,
  fallbackMenuItemCount: 0,
  includeEstablishments: false,
  inputProps: {},
  isLocked: false,
  readOnly: false,
  onEnter: function onEnter() {},
  renderDefault: null,
  renderFallback: null,
  size: "default",
  value: ""
};
AddressInput.DROPDOWN_TYPES = DROPDOWN_TYPES;
export default AddressInput;

function calcHighlightMax(_ref9) {
  var showSuggestions = _ref9.showSuggestions,
      enableSuggestions = _ref9.enableSuggestions,
      resultCount = _ref9.resultCount,
      defaultMenuItemCount = _ref9.defaultMenuItemCount,
      fallbackMenuItemCount = _ref9.fallbackMenuItemCount;
  var max = 0;

  if (showSuggestions) {
    max += resultCount;
  }

  if (enableSuggestions) {
    max += fallbackMenuItemCount;
  }

  if (!enableSuggestions) {
    max += defaultMenuItemCount;
  }

  return Math.max(max - 1, 0);
}